import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import BodyClassName from 'react-body-classname';

import { H1 } from 'components/core/Typography';
import { Text } from 'components/core/Text';
import { StarReviewV2 } from 'components/review/StarReviewV2';
import { Image } from 'components/core/Image';
import { PhoneNumber } from 'components/contact/PhoneNumber';
import { HoursAndDirectionsModal } from 'components/facility/HoursAndDirectionsModal';

import TrustedPartner from 'assets/images/logos/trusted-partner.svg';

import './facilityDetails.scss';

const ContactInfo = ({
	locationText,
	facilityPhoneNumber,
	unformattedFacilityPhoneNumber,
	reviewCount,
	reviewAverage,
	scrollToReviews,
	showPhoneNumber=true,
	googleReviewsEnabled,
	googleReviewsCount,
	googleReviewsAverage,
}) => (
	<div className="facility-contact-container">
		<Text className="facility-address">{locationText}</Text>
		{showPhoneNumber && (
			<div className="gray-dot" />
		)}
		{showPhoneNumber && ( 
			<BodyClassName className="sf_6128_click_to_show_phone_number_variation">
				<PhoneNumber
					link={true}
					phoneNumber={facilityPhoneNumber}
					unformattedPhoneNumber={unformattedFacilityPhoneNumber}
					size="small"
					hideBorder={false}
					clickable
				/>
			</BodyClassName>
		)}
		{reviewCount !== 0 && (
			<>
				<div className="gray-dot" />
				<StarReviewV2
					showOutOf
					outOf={5}
					reviewType="sparefoot"
					reviewCount={reviewCount || 0}
					rating={reviewAverage || 0}
					starSize="standard"
					variant="default"
					scrollToReviews={scrollToReviews}
				/>
			</>
		)}
		{
			googleReviewsEnabled && googleReviewsCount > 0 &&
			<>
				<div className="gray-dot" />
				<StarReviewV2
					showOutOf
					outOf={5}
					reviewType="google"
					reviewCount={googleReviewsCount || 0}
					rating={googleReviewsAverage || 0}
					labelSize="tiny"
					variant="default"
					scrollToReviews={scrollToReviews}
				/>
			</>
		}
	</div>
);

ContactInfo.propTypes = {
	locationText: PropTypes.string,
	facilityPhoneNumber: PropTypes.string,
	unformattedFacilityPhoneNumber: PropTypes.string,
	reviewCount: PropTypes.number,
	reviewAverage: PropTypes.number,
	scrollToReviews: PropTypes.func,
	showPhoneNumber: PropTypes.bool,
	googleReviewsEnabled: PropTypes.bool,
	googleReviewsCount: PropTypes.number,
	googleReviewsAverage: PropTypes.number,
};

export function FacilityDetails(props) {
	const [showingHoursAndDirections, setShowingHoursAndDirections] = useState(false);

	const toggleShowingHoursAndDirections = useCallback(() => {
		setShowingHoursAndDirections(prev => !prev);
	}, []);

	const contactInfoProps = {
		locationText: props.locationText,
		facilityPhoneNumber: props.facilityPhoneNumber,
		unformattedFacilityPhoneNumber: props.unformattedFacilityPhoneNumber,
		reviewCount: props.reviewCount,
		reviewAverage: props.reviewAverage,
		googleReviewsEnabled: props.googleReviewsEnabled,
		googleReviewsCount: props.googleReviewsCount,
		googleReviewsAverage: props.googleReviewsAverage,
		scrollToReviews: props.scrollToReviews,
		showPhoneNumber: props.showPhoneNumber,
	};

	return (
		<>
			<div className="facility-container">
				{props.isVipFacility ? (
					<div title="Reserve here with confidence. This is one of our most trusted brands." className="vip-facility-icon">
						<Image
							src={TrustedPartner}
							alt="Trusted Partner"
							className="trusted-partner-icon"
							title="Trusted Partner"
						/>
					</div>
				) : null}
				<div className='facility-details'>
					<H1
						size="medium"
						className="facility-name"
					>
						{props.facilityName}
					</H1>
					<div className="facility-contact-info-desktop">
						<ContactInfo {...contactInfoProps} />
					</div>
				</div>
			</div>
			{!props.isOutOfNetwork && (
				<HoursAndDirectionsModal
					facilityId={props.facilityId}
					showing={showingHoursAndDirections}
					onClose={toggleShowingHoursAndDirections}
					onSubmit={props.onGetHoursAndDirections}
				/>
			)}
			<div className="facility-contact-info-mobile">
				<ContactInfo {...contactInfoProps} />
			</div>
		</>
	);
}

FacilityDetails.propTypes = {
	facilityName: PropTypes.string.isRequired,
	facilityId: PropTypes.number.isRequired,
	reviewCount: PropTypes.number,
	facilityPhoneNumber: PropTypes.string,
	unformattedFacilityPhoneNumber: PropTypes.string,
	locationText: PropTypes.string,
	reviewAverage: PropTypes.number,
	isOutOfNetwork: PropTypes.bool,
	isVipFacility: PropTypes.bool,
	onGetHoursAndDirections: PropTypes.func.isRequired,
	getLocalPhoneNumberByZipCode: PropTypes.func,
	scrollToReviews: PropTypes.func,
	showPhoneNumber: PropTypes.bool,
	googleReviewsEnabled: PropTypes.bool,
	googleReviewsCount: PropTypes.number,
	googleReviewsAverage: PropTypes.number,
	facilityZipCode: PropTypes.string
};

export default FacilityDetails;
